/**
 * @file Helper functions for handling phone numbers.
 */
import { getCurrentCountryIso31662 } from '@@/bits/current_locale'
import type { CountryCode } from 'libphonenumber-js'

const formatPhoneNumber = async (
  phoneNumberString: string,
  countryCode = getCurrentCountryIso31662(),
): Promise<string> => {
  // lazy load libphonenumber-js since it is 95kb
  const { parsePhoneNumberFromString } = await import('libphonenumber-js')
  const phoneNumberNoWhitespace = phoneNumberString.replace(/\s/g, '')

  // if the phone number contains non-numeric characters and non presentational only chars (e.g. +, dashes, parentheses) skip formatting and return string as provided by user
  if (/[^\d+()-]/.test(phoneNumberNoWhitespace)) {
    return phoneNumberString
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumberNoWhitespace)
  if (parsedPhoneNumber != null) {
    return parsedPhoneNumber.formatNational()
  } else {
    // fallback in case user does not provide country code we assume it based on user's location
    const parsedPhoneNumberWithCountryCode = parsePhoneNumberFromString(
      phoneNumberNoWhitespace,
      countryCode as CountryCode,
    )
    return parsedPhoneNumberWithCountryCode != null
      ? parsedPhoneNumberWithCountryCode.formatNational()
      : phoneNumberString
  }
}

export { formatPhoneNumber }
